import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

interface TargetResult {
    gene_name: string;
    target_name: string;
    disorder: string;
}

interface StatsData {
    total_disorders: number;
    total_targets: number;
    total_variants: number;
    total_drugs: number;
}

function Home() {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [statsVisible, setStatsVisible] = useState(false);
    const [stats, setStats] = useState<StatsData | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [allTargets, setAllTargets] = useState<TargetResult[]>([]);
    const [filteredTargets, setFilteredTargets] = useState<TargetResult[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const statsRef = useRef<HTMLDivElement>(null);
    const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const searchWrapperRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const slides = [
        { image: '/homepage/1.jpg', title: 'Overview of MintDB Features' },
        { image: '/homepage/2.jpg', title: 'Variant Positional Classification' },
        { image: '/homepage/3.jpg', title: 'AI-Based Variant Pathogenicity Prediction' },
        { image: '/homepage/4.jpg', title: 'Geographic Allele Frequency Distribution' },
        { image: '/homepage/5.jpg', title: 'Identification of Variants Within Biobank' },
        { image: '/homepage/6.jpg', title: 'Overview of Features within Analysis Section' }
    ];

    const features = [
        {
            title: 'AI-Powered Queries',
            description: 'Use natural language to explore the database — no SQL knowledge required. Our AI translates your questions into precise queries.'
        },
        {
            title: 'Genetic Variants',
            description: 'Access extensive variant data with AlphaMissense pathogenicity predictions, ClinVar annotations, and population frequencies.'
        },
        {
            title: 'Target Analysis',
            description: 'Explore comprehensive drug target profiles with protein domains, 3D structures, and disorder associations.'
        },
        {
            title: 'Biobank Coverage',
            description: 'Discover variant representation across the Biobank with geographic allele frequency distributions from gnomAD.'
        },
        {
            title: 'Data Visualisation',
            description: 'Interactive lollipop plots, mutation heatmaps, frequency maps, and protein structure viewers for deep analysis.'
        },
        {
            title: 'SQL Guide',
            description: 'Learn to write powerful custom queries with our comprehensive guide and example templates.'
        }
    ];

    // Load targets from navbar cache or fetch fresh
    useEffect(() => {
        const loadTargets = async () => {
            const SEARCH_CACHE_KEY = 'mintdb_search_data';
            const CACHE_DURATION = 30 * 60 * 1000;

            try {
                // Try search-specific cache first (contains ALL targets, not truncated)
                const cachedData = localStorage.getItem(SEARCH_CACHE_KEY);
                if (cachedData) {
                    const { data, timestamp } = JSON.parse(cachedData);
                    if (Date.now() - timestamp < CACHE_DURATION && data.length > 0) {
                        setAllTargets(data);
                        return;
                    }
                }

                // Fetch all targets fresh
                const res = await fetch('/api/disorders');
                const disorders = await res.json();
                const flat: TargetResult[] = [];

                for (const disorder of disorders) {
                    try {
                        const tRes = await fetch(`/api/disorders?disorder=${encodeURIComponent(disorder.name.toLowerCase())}`);
                        if (!tRes.ok) continue;
                        const targets = await tRes.json();
                        if (Array.isArray(targets)) {
                            targets.forEach((t: any) => {
                                flat.push({
                                    gene_name: t.gene_name,
                                    target_name: t.target_name || '',
                                    disorder: disorder.name
                                });
                            });
                        }
                    } catch { /* skip */ }
                }
                setAllTargets(flat);

                // Cache all targets for search
                localStorage.setItem(SEARCH_CACHE_KEY, JSON.stringify({
                    data: flat,
                    timestamp: Date.now()
                }));
            } catch { /* fail silently */ }
        };
        loadTargets();
    }, []);

    // Fetch stats from API
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await fetch('/api/getData?type=stats');
                if (res.ok) {
                    const data = await res.json();
                    setStats(data);
                }
            } catch { /* fail silently, keep defaults */ }
        };
        fetchStats();
    }, []);

    // Filter targets as user types — one result per gene+disorder pair
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredTargets([]);
            setShowDropdown(false);
            return;
        }
        const q = searchQuery.toLowerCase();
        const seen = new Set<string>();
        const matching = allTargets.filter(t => {
            const key = `${t.gene_name}-${t.disorder}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return t.gene_name.toLowerCase().includes(q) ||
                   t.target_name.toLowerCase().includes(q);
        });

        // Sort: exact gene match first, then starts-with, then contains
        matching.sort((a, b) => {
            const aExact = a.gene_name.toLowerCase() === q ? 0 : 1;
            const bExact = b.gene_name.toLowerCase() === q ? 0 : 1;
            if (aExact !== bExact) return aExact - bExact;
            const aStarts = a.gene_name.toLowerCase().startsWith(q) ? 0 : 1;
            const bStarts = b.gene_name.toLowerCase().startsWith(q) ? 0 : 1;
            if (aStarts !== bStarts) return aStarts - bStarts;
            return a.gene_name.localeCompare(b.gene_name) || a.disorder.localeCompare(b.disorder);
        });

        const matches = matching.slice(0, 20);
        setFilteredTargets(matches);
        setShowDropdown(matches.length > 0);
        setHighlightedIndex(-1);
    }, [searchQuery, allTargets]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const navigateToTarget = (target: TargetResult) => {
        setSearchQuery('');
        setShowDropdown(false);
        navigate(`/disorders/${encodeURIComponent(target.disorder.toLowerCase())}?gene=${encodeURIComponent(target.gene_name)}`);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showDropdown) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev => Math.min(prev + 1, filteredTargets.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && highlightedIndex >= 0) {
            e.preventDefault();
            navigateToTarget(filteredTargets[highlightedIndex]);
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    const goToSlide = (index: number) => {
        setCurrentSlide(index);
        if (autoplayRef.current) clearInterval(autoplayRef.current);
        autoplayRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
    };

    // Slideshow autoplay
    useEffect(() => {
        autoplayRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
        return () => { if (autoplayRef.current) clearInterval(autoplayRef.current); };
    }, [slides.length]);

    // Stats section intersection observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
            { threshold: 0.3 }
        );
        if (statsRef.current) observer.observe(statsRef.current);
        return () => observer.disconnect();
    }, []);

    // Shared slideshow renderer
    const renderSlideshow = (id: string) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{
                position: 'relative',
                width: '100%',
                paddingBottom: '42%',
                backgroundColor: '#ffffff',
                borderRadius: '8px 8px 0 0',
                overflow: 'hidden'
            }}>
                {slides.map((slide, i) => (
                    <img
                        key={`${id}-${i}`}
                        src={slide.image}
                        alt={slide.title}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center',
                            opacity: currentSlide === i ? 1 : 0,
                            transition: 'opacity 0.6s ease-in-out',
                            pointerEvents: 'none'
                        }}
                    />
                ))}
            </div>
            <div style={{
                backgroundColor: '#1A2940',
                padding: '0.65rem 1rem',
                borderRadius: '0 0 8px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.75rem',
                minHeight: '40px'
            }}>
                <p style={{
                    color: '#D1E0F4',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                    margin: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {slides[currentSlide].title}
                </p>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    {slides.map((_, i) => (
                        <div
                            key={i}
                            onClick={() => goToSlide(i)}
                            style={{
                                width: currentSlide === i ? '18px' : '7px',
                                height: '7px',
                                borderRadius: '4px',
                                backgroundColor: currentSlide === i ? '#60a5fa' : 'rgba(255,255,255,0.3)',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );

    // Highlight matching text in search results
    const highlightMatch = (text: string, query: string) => {
        if (!query.trim()) return text;
        const idx = text.toLowerCase().indexOf(query.toLowerCase());
        if (idx === -1) return text;
        return (
            <>
                {text.slice(0, idx)}
                <span style={{ color: '#60a5fa', fontWeight: '700' }}>{text.slice(idx, idx + query.length)}</span>
                {text.slice(idx + query.length)}
            </>
        );
    };

    return (
        <div style={{ backgroundColor: '#f0f2f5' }}>
            <style>{`
                @keyframes countUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .stat-animate {
                    animation: countUp 0.6s ease-out forwards;
                    opacity: 0;
                }
                .cta-btn {
                    transition: all 0.25s ease;
                }
                .cta-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(96, 165, 250, 0.35);
                }
                .cta-btn-outline:hover {
                    background-color: rgba(255,255,255,0.15) !important;
                    transform: translateY(-2px);
                }
                .target-search-input {
                    width: 100%;
                    background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.25);
                    border-radius: 8px;
                    padding: 0.9rem 1.2rem 0.9rem 3rem;
                    color: #ffffff;
                    font-size: clamp(0.95rem, 1.5vw, 1.05rem);
                    outline: none;
                    transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
                }
                .target-search-input::placeholder {
                    color: #94a3b8;
                }
                .target-search-input:focus {
                    border-color: #60a5fa;
                    background: rgba(255,255,255,0.18);
                    box-shadow: 0 0 0 3px rgba(96,165,250,0.15);
                }
                .overview-feature-item {
                    transition: background 0.2s ease;
                    border-radius: 5px;
                }
                .overview-feature-item:hover {
                    background: rgba(96,165,250,0.06);
                }
                .search-result-item {
                    padding: 0.65rem 1rem;
                    cursor: pointer;
                    transition: background 0.15s ease;
                    border-bottom: 1px solid #f1f5f9;
                }
                .search-result-item:last-child {
                    border-bottom: none;
                }
                .search-result-item:hover,
                .search-result-item.highlighted {
                    background: #f1f5f9;
                }
            `}</style>

            {/* ====== HERO SECTION ====== */}
            <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
                padding: 'clamp(3rem, 6vh, 4.5rem) 0'
            }}>
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'url("/hompage%20background5.png")',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    zIndex: 0
                }} />
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(26,41,64,0.93) 0%, rgba(26,41,64,0.85) 100%)',
                    zIndex: 1
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{
                        textAlign: 'center',
                        maxWidth: '700px',
                        margin: '0 auto'
                    }}>
                        <h1 style={{
                            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                            fontWeight: '700',
                            color: '#ffffff',
                            lineHeight: '1.1',
                            marginBottom: '0.6rem',
                            letterSpacing: '-0.02em'
                        }}>
                            Mint<span style={{ color: '#60a5fa' }}>DB</span>
                        </h1>

                        <p style={{
                            fontSize: 'clamp(0.95rem, 1.6vw, 1.1rem)',
                            color: '#D1E0F4',
                            lineHeight: '1.5',
                            marginBottom: '2rem',
                            fontWeight: '400'
                        }}>
                            <span style={{ color: '#60a5fa', fontWeight: 700 }}>M</span>issense-Mutation{' '}
                            <span style={{ color: '#60a5fa', fontWeight: 700 }}>I</span>n{' '}
                            <span style={{ color: '#60a5fa', fontWeight: 700 }}>N</span>europsychiatric Drug{' '}
                            <span style={{ color: '#60a5fa', fontWeight: 700 }}>T</span>argets{' '}
                            <span style={{ color: '#60a5fa', fontWeight: 700 }}>D</span>ata<span style={{ color: '#60a5fa', fontWeight: 700 }}>B</span>ase
                        </p>

                        {/* Target Search — centred and prominent */}
                        <div ref={searchWrapperRef} style={{
                            position: 'relative',
                            maxWidth: '560px',
                            margin: '0 auto 1.5rem',
                            zIndex: 9999
                        }}>
                            <div style={{ position: 'relative' }}>
                                <svg
                                    width="18" height="18" fill="none" stroke="#94a3b8" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
                                    style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                                >
                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <input
                                    type="text"
                                    className="target-search-input"
                                    placeholder="Search for a drug target or gene name..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={() => { if (filteredTargets.length > 0) setShowDropdown(true); }}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>

                            {/* Autocomplete Dropdown */}
                            {showDropdown && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    marginTop: '4px',
                                    backgroundColor: '#ffffff',
                                    borderRadius: '8px',
                                    boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
                                    border: '1px solid #e2e8f0',
                                    maxHeight: '600px',
                                    overflowY: 'auto',
                                    zIndex: 9999,
                                    textAlign: 'left'
                                }}>
                                    {filteredTargets.map((target, i) => (
                                        <div
                                            key={`${target.gene_name}-${i}`}
                                            className={`search-result-item ${i === highlightedIndex ? 'highlighted' : ''}`}
                                            onClick={() => navigateToTarget(target)}
                                        >
                                            <div style={{ fontSize: '0.85rem', color: '#1A2940', fontWeight: '600' }}>
                                                {highlightMatch(target.gene_name, searchQuery)}
                                                {target.target_name && (
                                                    <span style={{ fontWeight: '400', color: '#64748b' }}>
                                                        {' '} — {highlightMatch(target.target_name, searchQuery)}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                {target.disorder}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <p style={{
                            fontSize: '0.8rem',
                            color: '#64748b',
                            marginBottom: '2rem'
                        }}>
                            e.g. DRD2, HTR2A, SLC6A4, GRIN1
                        </p>

                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="/analysis" className="cta-btn" style={{
                                display: 'inline-block',
                                backgroundColor: '#60a5fa',
                                color: '#ffffff',
                                padding: '0.65rem 1.5rem',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontWeight: '600',
                                fontSize: '0.85rem'
                            }}>
                                Explore Database
                            </Link>
                            <Link to="/sources" className="cta-btn cta-btn-outline" style={{
                                display: 'inline-block',
                                backgroundColor: 'transparent',
                                color: '#D1E0F4',
                                padding: '0.65rem 1.5rem',
                                borderRadius: '6px',
                                border: '1px solid rgba(209, 224, 244, 0.3)',
                                textDecoration: 'none',
                                fontWeight: '500',
                                fontSize: '0.85rem',
                                transition: 'all 0.25s ease'
                            }}>
                                View Sources
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* ====== STATS BAR ====== */}
            <div ref={statsRef} style={{
                backgroundColor: '#1A2940',
                borderBottom: '3px solid #60a5fa',
                padding: '1.5rem 0'
            }}>
                <div className="container">
                    <div className="row g-3 text-center">
                        {[
                            { value: stats ? stats.total_disorders.toLocaleString() : '—', label: 'Disorders' },
                            { value: stats ? stats.total_targets.toLocaleString() : '—', label: 'Drug Targets' },
                            { value: stats ? stats.total_variants.toLocaleString() : '—', label: 'Genetic Variants' },
                            { value: stats ? stats.total_drugs.toLocaleString() : '—', label: 'Drugs' }
                        ].map((stat, i) => (
                            <div className="col-6 col-md-3" key={i}>
                                <div className={statsVisible ? 'stat-animate' : ''} style={{
                                    animationDelay: `${i * 0.15}s`
                                }}>
                                    <div style={{
                                        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                                        fontWeight: '700',
                                        color: '#60a5fa',
                                        lineHeight: '1.2'
                                    }}>
                                        {stat.value}
                                    </div>
                                    <div style={{
                                        fontSize: '0.75rem',
                                        color: '#94a3b8',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                        fontWeight: '500',
                                        marginTop: '0.25rem'
                                    }}>
                                        {stat.label}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ====== PLATFORM OVERVIEW ====== */}
            <div style={{ padding: '2.5rem 0 1.5rem' }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12">
                                <div className="row g-4" style={{ alignItems: 'stretch' }}>

                                    {/* Slideshow — left side */}
                                    <div className="col-lg-8">
                                        <div style={{
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            border: '1px solid #e2e8f0',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                        }}>
                                            <div style={{
                                                position: 'relative',
                                                width: '100%',
                                                paddingBottom: '55%',
                                                backgroundColor: '#ffffff',
                                                borderRadius: '8px 8px 0 0',
                                                overflow: 'hidden'
                                            }}>
                                                {slides.map((slide, i) => (
                                                    <img
                                                        key={`overview-${i}`}
                                                        src={slide.image}
                                                        alt={slide.title}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            objectPosition: 'center',
                                                            opacity: currentSlide === i ? 1 : 0,
                                                            transition: 'opacity 0.6s ease-in-out',
                                                            pointerEvents: 'none'
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                            <div style={{
                                                backgroundColor: '#1A2940',
                                                padding: '0.65rem 1rem',
                                                borderRadius: '0 0 8px 8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '0.75rem',
                                                minHeight: '40px'
                                            }}>
                                                <p style={{
                                                    color: '#D1E0F4',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '500',
                                                    margin: 0,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {slides[currentSlide].title}
                                                </p>
                                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                                    {slides.map((_, i) => (
                                                        <div
                                                            key={i}
                                                            onClick={() => goToSlide(i)}
                                                            style={{
                                                                width: currentSlide === i ? '18px' : '7px',
                                                                height: '7px',
                                                                borderRadius: '4px',
                                                                backgroundColor: currentSlide === i ? '#60a5fa' : 'rgba(255,255,255,0.3)',
                                                                transition: 'all 0.3s ease',
                                                                cursor: 'pointer'
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Features — right side, cardless */}
                                    <div className="col-lg-4 d-flex">
                                        <div style={{ paddingLeft: 'clamp(0rem, 1vw, 0.5rem)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%' }}>
                                            <div>
                                            <p style={{
                                                fontSize: '0.6rem',
                                                fontWeight: '700',
                                                color: '#60a5fa',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.12em',
                                                marginBottom: '0.15rem'
                                            }}>
                                                Features
                                            </p>
                                            <h2 style={{
                                                fontSize: '1.1rem',
                                                fontWeight: '700',
                                                color: '#1A2940',
                                                marginBottom: '0.2rem',
                                                lineHeight: '1.25'
                                            }}>
                                                Platform Overview
                                            </h2>
                                            <p style={{
                                                fontSize: '0.7rem',
                                                color: '#64748b',
                                                lineHeight: '1.5',
                                                marginBottom: '0.6rem'
                                            }}>
                                                Explore variant classifications, biobank data, and analysis features for neuropsychiatric drug targets.
                                            </p>

                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                {features.map((feat, i) => (
                                                    <div key={i} className="overview-feature-item" style={{
                                                        display: 'flex',
                                                        gap: '0.55rem',
                                                        alignItems: 'flex-start',
                                                        padding: '0.25rem 0.5rem',
                                                        margin: '0 -0.5rem'
                                                    }}>
                                                        <div style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'rgba(96,165,250,0.1)',
                                                            border: '1.5px solid rgba(96,165,250,0.3)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                            marginTop: '1px'
                                                        }}>
                                                            <span style={{
                                                                fontSize: '0.55rem',
                                                                fontWeight: '700',
                                                                color: '#60a5fa'
                                                            }}>
                                                                {String(i + 1).padStart(2, '0')}
                                                            </span>
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: '600',
                                                                color: '#1A2940',
                                                                lineHeight: '1.3'
                                                            }}>
                                                                {feat.title}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '0.65rem',
                                                                color: '#94a3b8',
                                                                lineHeight: '1.4'
                                                            }}>
                                                                {feat.description}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            </div>

                                            {/* Latest Release — own card */}
                                            <div style={{
                                                borderRadius: '6px',
                                                border: '1px solid #e2e8f0',
                                                overflow: 'hidden',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                            }}>
                                                <div style={{
                                                    backgroundColor: '#1A2940',
                                                    padding: '0.35rem 0.65rem',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}>
                                                    <h3 style={{
                                                        fontSize: '0.6rem',
                                                        fontWeight: '700',
                                                        color: '#ffffff',
                                                        margin: 0,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.1em'
                                                    }}>
                                                        Latest Release
                                                    </h3>
                                                    <span style={{
                                                        fontSize: '0.55rem',
                                                        color: '#60a5fa',
                                                        fontWeight: '700',
                                                        backgroundColor: 'rgba(96,165,250,0.12)',
                                                        padding: '0.1rem 0.4rem',
                                                        borderRadius: '10px',
                                                        letterSpacing: '0.03em'
                                                    }}>
                                                        Oct 20, 2025
                                                    </span>
                                                </div>
                                                <div style={{ backgroundColor: '#ffffff', padding: '0.5rem 0.75rem' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                                        {[
                                                            'Enhanced Target & Disorder Analysis with improved biobank metrics',
                                                            'Updated genetic variant database with AlphaMissense classifications',
                                                            'Expanded drug-target interaction data from multiple sources',
                                                            'Advanced protein structure visualisations and binding analysis'
                                                        ].map((item, i) => (
                                                            <div key={i} style={{
                                                                fontSize: '0.65rem',
                                                                color: '#64748b',
                                                                lineHeight: '1.45',
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                gap: '0.4rem'
                                                            }}>
                                                                <span style={{
                                                                    color: '#60a5fa',
                                                                    fontWeight: '700',
                                                                    flexShrink: 0,
                                                                    marginTop: '1px'
                                                                }}>&#x2022;</span>
                                                                {item}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Home;
